/**
 * File Upload Component
 *
 * Drag-and-drop file upload component with preview
 *
 * @module components/common/FileUpload
 */

import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, Button, ListGroup, Badge, ProgressBar } from 'react-bootstrap';

/**
 * FileUpload component
 * @param {Object} props
 * @param {Function} props.onFilesSelected - Callback when files are selected
 * @param {Array<string>} props.acceptedTypes - Accepted file MIME types
 * @param {number} props.maxSize - Max file size in bytes
 * @param {number} props.maxFiles - Max number of files
 * @param {boolean} props.multiple - Allow multiple files
 */
const FileUpload = ({
  onFilesSelected,
  acceptedTypes = ['image/*', 'application/pdf'],
  maxSize = 10485760, // 10MB
  maxFiles = 5,
  multiple = true,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress] = useState({});

  const onDrop = useCallback(
    (acceptedFiles) => {
      const newFiles = acceptedFiles.map((file) => ({
        file,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        id: Math.random().toString(36).substring(7),
      }));

      setSelectedFiles((prev) => [...prev, ...newFiles].slice(0, maxFiles));
      onFilesSelected && onFilesSelected(newFiles.map((f) => f.file));
    },
    [maxFiles, onFilesSelected]
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: acceptedTypes.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
    maxSize,
    maxFiles,
    multiple,
  });

  const removeFile = (id) => {
    setSelectedFiles((prev) => {
      const updated = prev.filter((f) => f.id !== id);
      onFilesSelected && onFilesSelected(updated.map((f) => f.file));
      return updated;
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="file-upload">
      <div
        {...getRootProps()}
        className={`dropzone p-4 border border-2 border-dashed rounded text-center ${
          isDragActive ? 'border-primary bg-light' : 'border-secondary'
        }`}
        style={{ cursor: 'pointer' }}
      >
        <input {...getInputProps()} />
        <div className="py-3">
          <i className="bi bi-cloud-upload fs-1 text-muted d-block mb-3"></i>
          {isDragActive ? (
            <p className="mb-0 text-primary">Suelta los archivos aquí...</p>
          ) : (
            <>
              <p className="mb-2">
                <strong>Arrastra archivos aquí</strong> o haz clic para seleccionar
              </p>
              <p className="text-muted small mb-0">
                Máximo {maxFiles} archivo(s) • Tamaño máximo: {formatFileSize(maxSize)}
              </p>
            </>
          )}
        </div>
      </div>

      {fileRejections.length > 0 && (
        <div className="alert alert-danger mt-3">
          <strong>Archivos rechazados:</strong>
          <ul className="mb-0 mt-2">
            {fileRejections.map(({ file, errors }) => (
              <li key={file.name}>
                {file.name} - {errors.map((e) => e.message).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {selectedFiles.length > 0 && (
        <Card className="mt-3">
          <Card.Header>
            <strong>Archivos seleccionados ({selectedFiles.length})</strong>
          </Card.Header>
          <ListGroup variant="flush">
            {selectedFiles.map((item) => (
              <ListGroup.Item key={item.id} className="d-flex align-items-center">
                {item.preview && (
                  <img
                    src={item.preview}
                    alt={item.file.name}
                    className="me-3 rounded"
                    style={{ width: 50, height: 50, objectFit: 'cover' }}
                  />
                )}
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center">
                    <strong className="text-truncate" style={{ maxWidth: '300px' }}>
                      {item.file.name}
                    </strong>
                    <Badge bg="secondary" className="ms-2">
                      {formatFileSize(item.file.size)}
                    </Badge>
                  </div>
                  {uploadProgress[item.id] !== undefined && (
                    <ProgressBar
                      now={uploadProgress[item.id]}
                      variant="success"
                      className="mt-2"
                      style={{ height: '6px' }}
                    />
                  )}
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="ms-3"
                  onClick={() => removeFile(item.id)}
                >
                  <i className="bi bi-trash"></i>
                </Button>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card>
      )}
    </div>
  );
};

export default FileUpload;
